
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Send, Edit, Clock } from 'lucide-react';
import { useNodes, useSubmitForReview } from '@/hooks/useNodes';
import { useAuth } from '@/hooks/useAuth';
import { useRoleAccess } from '@/hooks/useRoleAccess';
import NodeEditor from './NodeEditor';
import NodeCard from './NodeCard';
import { toast } from 'sonner';
import type { Tables } from '@/integrations/supabase/types';

type Node = Tables<'nodes'>;

const ContributorWorkflow: React.FC = () => {
  const { user } = useAuth();
  const { canCreateContent } = useRoleAccess();
  const { data: allNodes } = useNodes();
  const submitForReviewMutation = useSubmitForReview();
  
  const [showEditor, setShowEditor] = useState(false);
  const [editingNode, setEditingNode] = useState<Node | undefined>();

  // Filter nodes by current user and status
  const myNodes = allNodes?.filter(node => node.author_id === user?.id) || [];
  const draftNodes = myNodes.filter(node => node.status === 'draft');
  const reviewNodes = myNodes.filter(node => node.status === 'in_review');
  const publishedNodes = myNodes.filter(node => node.status === 'published');

  const handleCreateNew = () => {
    setEditingNode(undefined);
    setShowEditor(true);
  };

  const handleEditDraft = (node: Node) => {
    setEditingNode(node);
    setShowEditor(true);
  };

  const handleSubmitForReview = async (nodeId: string) => {
    try {
      await submitForReviewMutation.mutateAsync({ nodeId });
      toast.success('Content submitted for review successfully');
    } catch (error) {
      console.error('Error submitting for review:', error);
      toast.error('Failed to submit content for review');
    }
  };

  const handleSaveComplete = () => {
    setShowEditor(false);
    setEditingNode(undefined);
  };

  if (!canCreateContent) {
    return (
      <div className="text-center py-8">
        <h2 className="text-xl font-semibold mb-2">Access Restricted</h2>
        <p className="text-black">
          You need contributor access or higher to create content.
        </p>
      </div>
    );
  }

  if (showEditor) {
    return (
      <NodeEditor
        node={editingNode}
        onSave={handleSaveComplete}
        onCancel={() => setShowEditor(false)}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">My Contributions</h2>
          <p className="text-black">
            Create, edit, and submit content for review
          </p>
        </div>
        <Button onClick={handleCreateNew}>
          <Plus className="h-4 w-4 mr-2" />
          Create Content
        </Button>
      </div>

      <Tabs defaultValue="drafts" className="space-y-4">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="drafts">
            Drafts ({draftNodes.length})
          </TabsTrigger>
          <TabsTrigger value="review">
            In Review ({reviewNodes.length})
          </TabsTrigger>
          <TabsTrigger value="published">
            Published ({publishedNodes.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="drafts" className="space-y-4">
          {draftNodes.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {draftNodes.map((node) => (
                <div key={node.id} className="relative">
                  <NodeCard node={node} />
                  <div className="absolute top-2 right-2 flex gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEditDraft(node)}
                    >
                      <Edit className="h-3 w-3 mr-1" />
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleSubmitForReview(node.id)}
                      disabled={submitForReviewMutation.isPending}
                    >
                      <Send className="h-3 w-3 mr-1" />
                      Submit
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-black mb-4">No draft content</p>
              <Button onClick={handleCreateNew}>
                <Plus className="h-4 w-4 mr-2" />
                Create Your First Draft
              </Button>
            </div>
          )}
        </TabsContent>

        <TabsContent value="review" className="space-y-4">
          {reviewNodes.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {reviewNodes.map((node) => (
                <div key={node.id} className="relative">
                  <NodeCard node={node} />
                  <Badge className="absolute top-2 right-2" variant="secondary">
                    <Clock className="h-3 w-3 mr-1" />
                    In Review
                  </Badge>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-black">
                No content currently under review
              </p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="published" className="space-y-4">
          {publishedNodes.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {publishedNodes.map((node) => (
                <div key={node.id} className="relative">
                  <NodeCard node={node} />
                  <Badge className="absolute top-2 right-2" variant="default">
                    Published
                  </Badge>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-black">
                No published content yet
              </p>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ContributorWorkflow;
